import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Badge, Card, Skeleton } from '@/design-system/primitives'
import { cn } from '@/lib/cn'
import { formatMoney } from '@/lib/money'

import { useAdminMutation, useAdminQuery } from '../api'
import { AdminHeader } from '../AdminLayout'
import { MoneyField, NumberField, SelectField, TextField } from '../components/fields'
import { SellersPanel } from '../components/SellersPanel'
import { useAdminContext } from '../session'
import type { SalesForecast, SalesTarget, TargetKind } from '../types'

const KINDS: TargetKind[] = [
  'ticket',
  'drink_table',
  'food_item',
  'food_pack',
  'drink_item',
  'extra',
]

/**
 * Ce qu'on vise, et où on en est.
 *
 * Le budget disait ce qu'on allait dépenser. Rien ne disait ce qu'on comptait
 * vendre — et sans cette ligne d'horizon, un total d'encaissements ne veut
 * rien dire : quinze mille euros est un triomphe ou une déception selon ce
 * qu'on visait.
 *
 * Trois partis pris gouvernent l'écran :
 *
 * * **on saisit une quantité et un prix**, jamais un montant. « 40 tables à
 *   200 € » se relit, se corrige, se compare à ce qui est parti ; « 8 000 € de
 *   tables » ne dit ni combien ni à quel prix, et le jour où l'une des deux
 *   hypothèses bouge, plus personne ne sait laquelle.
 * * **le cap et le détail restent deux chiffres distincts.** Viser plus haut
 *   que ce qu'on a su détailler est une décision de gestion ; les confondre
 *   effacerait cette décision.
 * * **le réalisé global ne s'additionne pas depuis les lignes.** Un objectif
 *   « toute la nourriture » et un objectif « Poulet DG » décrivent en partie
 *   la même vente. Le total vient de la caisse ; les lignes situent chaque
 *   poste, elles ne composent pas le résultat.
 */
export function AdminForecastPage() {
  const { t, i18n } = useTranslation()
  const { eventId } = useAdminContext()
  const [adding, setAdding] = useState(false)

  useEffect(() => {
    document.title = t('admin.section.forecast')
  }, [t])

  const query = useAdminQuery<SalesForecast>(
    ['admin-forecast', eventId],
    `/admin/events/${eventId}/forecast`,
    eventId,
  )

  const report = query.data

  const money = useMemo(
    () => (minor: number) =>
      formatMoney(
        { amount_minor: minor, currency: report?.currency ?? 'EUR' },
        i18n.language,
      ),
    [report?.currency, i18n.language],
  )

  if (query.isPending) return <Skeleton className="h-96 w-full" />
  if (query.isError) {
    return <p className="text-sm text-state-danger">{query.error.message}</p>
  }
  if (!report) return null

  const reached =
    report.goal_minor > 0
      ? Math.round((report.realized_minor * 100) / report.goal_minor)
      : 0

  return (
    <>
      <AdminHeader
        title={t('admin.section.forecast')}
        description={t('admin.forecast.intro')}
      />

      <GoalCard report={report} eventId={eventId} money={money} reached={reached} />

      <div className="mt-8 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h2 className="font-display text-heading-md text-content-primary">
            {t('admin.forecast.linesTitle')}
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-content-secondary">
            {t('admin.forecast.linesHint')}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setAdding(!adding)}>
          {adding ? t('admin.project.cancel') : t('admin.forecast.add')}
        </Button>
      </div>

      {adding ? (
        <TargetForm
          currency={report.currency}
          eventId={eventId}
          onDone={() => setAdding(false)}
        />
      ) : null}

      {report.lines.length === 0 && !adding ? (
        <p className="mt-5 text-sm text-content-secondary">
          {t('admin.forecast.empty')}
        </p>
      ) : null}

      <div className="mt-5 grid gap-2">
        {report.lines.map((line) => (
          <TargetRow
            key={line.id}
            line={line}
            currency={report.currency}
            eventId={eventId}
            money={money}
          />
        ))}
      </div>

      <SellersPanel eventId={eventId} />
    </>
  )
}

/** Le cap d'ensemble, et la distance qui reste à parcourir. */
function GoalCard({
  report,
  eventId,
  money,
  reached,
}: {
  report: SalesForecast
  eventId: string | undefined
  money: (minor: number) => string
  reached: number
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [goal, setGoal] = useState<number | null>(report.goal_minor)
  const [attendance, setAttendance] = useState<number | null>(
    report.expected_attendance,
  )

  const save = useAdminMutation<
    SalesForecast,
    { goal_minor: number; expected_attendance: number | null; notes: string | null }
  >('put', () => `/admin/events/${eventId}/forecast/goal`, eventId, [
    ['admin-forecast', eventId ?? ''],
  ])

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-caption uppercase tracking-wide text-content-muted">
            {t('admin.forecast.goal')}
          </p>
          <p className="mt-1 font-display text-heading-lg tabular-nums text-content-primary">
            {money(report.goal_minor)}
          </p>
          {report.expected_attendance !== null ? (
            <p className="mt-1 text-caption text-content-muted">
              {t('admin.forecast.attendance', { count: report.expected_attendance })}
            </p>
          ) : null}
        </div>

        <div className="min-w-0 sm:text-right">
          <p className="text-caption uppercase tracking-wide text-content-muted">
            {t('admin.forecast.realized')}
          </p>
          <p className="mt-1 font-display text-heading-lg tabular-nums text-content-primary">
            {money(report.realized_minor)}
          </p>
          <p className="mt-1 text-caption text-content-muted">
            {report.gap_minor > 0
              ? t('admin.forecast.remaining', { amount: money(report.gap_minor) })
              : t('admin.forecast.reached')}
          </p>
        </div>

        <Button size="sm" variant="outline" onClick={() => setOpen(!open)}>
          {open ? t('admin.project.cancel') : t('admin.forecast.editGoal')}
        </Button>
      </div>

      <Progress percent={reached} />

      {/* Le cap annoncé et la somme des lignes sont deux chiffres différents,
          et cet écart est une décision : on vise souvent un peu plus haut que
          ce qu'on a su détailler. Les confondre l'effacerait. */}
      <p className="mt-3 text-caption text-content-muted">
        {t('admin.forecast.plannedVsGoal', {
          planned: money(report.planned_minor),
          goal: money(report.goal_minor),
        })}
      </p>

      {open ? (
        <div className="mt-5 grid gap-4 border-t border-line-subtle pt-5 sm:grid-cols-2">
          <MoneyField
            label={t('admin.forecast.goal')}
            hint={t('admin.forecast.goalHint')}
            minor={goal}
            currency={report.currency}
            onChange={setGoal}
          />
          <NumberField
            label={t('admin.forecast.attendanceLabel')}
            hint={t('admin.forecast.attendanceHint')}
            value={attendance}
            onChange={setAttendance}
            min={0}
          />
          {save.error ? (
            <p className="text-sm text-state-danger sm:col-span-2">
              {save.error.message}
            </p>
          ) : null}
          <div className="sm:col-span-2">
            <Button
              size="sm"
              disabled={save.isPending}
              onClick={() =>
                save.mutate(
                  {
                    goal_minor: goal ?? 0,
                    expected_attendance: attendance,
                    notes: report.notes,
                  },
                  { onSuccess: () => setOpen(false) },
                )
              }
            >
              {t('admin.forecast.save')}
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  )
}

/**
 * La barre d'avancement.
 *
 * Elle se remplit au-delà de cent pour cent sans déborder de son cadre : un
 * objectif dépassé est la bonne nouvelle de la soirée, et il doit se voir
 * autrement qu'en restant collé au bout de la barre.
 */
function Progress({ percent }: { percent: number }) {
  return (
    <div className="mt-4">
      <div className="h-2 w-full overflow-hidden rounded-full bg-bg-overlay">
        <div
          className={cn(
            'h-full rounded-full transition-[width]',
            percent >= 100 ? 'bg-state-success' : 'bg-[var(--champagne-400)]',
          )}
          style={{ width: `${Math.min(Math.max(percent, 0), 100)}%` }}
        />
      </div>
      <p className="mt-1 text-caption tabular-nums text-content-muted">{percent} %</p>
    </div>
  )
}

/** Une ligne d'objectif : ce qu'on visait, ce qui est parti. */
function TargetRow({
  line,
  currency,
  eventId,
  money,
}: {
  line: SalesTarget
  currency: string
  eventId: string | undefined
  money: (minor: number) => string
}) {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const [label, setLabel] = useState(line.label)
  const [refId, setRefId] = useState(line.ref_id ?? '')
  const [quantity, setQuantity] = useState<number | null>(line.target_quantity)
  const [price, setPrice] = useState<number | null>(line.unit_price_minor)
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Le catalogue ne se lit qu'une fois la ligne ouverte : une page de vingt
  // objectifs ne fait pas vingt requêtes pour rien.
  const products = useAdminQuery<TargetProduct[]>(
    ['admin-forecast-products', eventId, line.item_type, i18n.language],
    `/admin/events/${eventId}/forecast/products?item_type=${line.item_type}&locale=${i18n.language}`,
    eventId,
    { enabled: open },
  ).data ?? []

  const save = useAdminMutation<
    SalesTarget,
    {
      ref_id: string | null
      label: string
      target_quantity: number
      unit_price_minor: number
    }
  >('patch', () => `/admin/events/${eventId}/forecast/lines/${line.id}`, eventId, [
    ['admin-forecast', eventId ?? ''],
  ])

  const remove = useAdminMutation<{ message: string }, Record<string, never>>(
    'delete',
    () => `/admin/events/${eventId}/forecast/lines/${line.id}`,
    eventId,
    [['admin-forecast', eventId ?? '']],
  )

  return (
    <Card className="p-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full px-4 py-3 text-left"
      >
        <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="min-w-0 flex-1 truncate text-content-primary">
            {line.label}
          </span>
          <span className="shrink-0 tabular-nums text-content-secondary">
            {line.sold_quantity} / {line.target_quantity}
          </span>
          <span className="shrink-0 font-display text-heading-sm tabular-nums text-content-primary">
            {money(line.sold_revenue_minor)}
          </span>
          <Badge tone={line.progress_percent >= 100 ? 'success' : 'neutral'}>
            {line.progress_percent} %
          </Badge>
        </span>
        <span className="mt-1 block text-caption text-content-muted">
          {t(`admin.forecast.kinds.${line.item_type}`)}
          {line.ref_id === null ? ` · ${t('admin.forecast.wholeFamily')}` : ''}
          {' · '}
          {t('admin.forecast.rowTarget', {
            quantity: line.target_quantity,
            price: money(line.unit_price_minor),
            total: money(line.target_revenue_minor),
          })}
        </span>
      </button>

      {open ? (
        <div className="grid gap-4 border-t border-line-subtle px-4 py-4 sm:grid-cols-3">
          <div className="sm:col-span-3">
            <ProductField products={products} value={refId} onChange={setRefId} />
          </div>
          <TextField
            label={t('admin.forecast.label')}
            value={label}
            onChange={setLabel}
          />
          <NumberField
            label={t('admin.forecast.quantity')}
            value={quantity}
            onChange={setQuantity}
            min={0}
          />
          <MoneyField
            label={t('admin.forecast.unitPrice')}
            minor={price}
            currency={currency}
            onChange={setPrice}
          />

          {save.error ? (
            <p className="text-sm text-state-danger sm:col-span-3">
              {save.error.message}
            </p>
          ) : null}
          {remove.error ? (
            <p className="text-sm text-state-danger sm:col-span-3">
              {remove.error.message}
            </p>
          ) : null}

          <div className="flex flex-wrap gap-2 sm:col-span-3">
            <Button
              size="sm"
              disabled={save.isPending}
              onClick={() =>
                save.mutate(
                  {
                    ref_id: refId || null,
                    label,
                    target_quantity: quantity ?? 0,
                    unit_price_minor: price ?? 0,
                  },
                  { onSuccess: () => setOpen(false) },
                )
              }
            >
              {t('admin.forecast.save')}
            </Button>

            {/* Un objectif est une prévision : il se supprime, et changer
                d'avis fait partie du travail. La confirmation tient à un
                second clic, pas à une boîte de dialogue — ce qu'on efface ici
                n'est ni une vente ni une écriture comptable. */}
            {confirmDelete ? (
              <Button
                size="sm"
                variant="ember"
                disabled={remove.isPending}
                onClick={() => remove.mutate({})}
              >
                {t('admin.forecast.confirmDelete')}
              </Button>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)}>
                {t('admin.forecast.delete')}
              </Button>
            )}
          </div>
        </div>
      ) : null}
    </Card>
  )
}

/** Un produit qu'un objectif peut viser (`GET /forecast/products`). */
interface TargetProduct {
  id: string
  label: string
  price_minor: number
}

/**
 * Le catalogue d'une famille, pour viser un produit précis.
 *
 * Sans ce choix, chaque ligne comptait toute sa famille : deux lignes
 * « Tables » affichaient chacune toutes les tables vendues. Lu sur une route
 * du prévisionnel, pas sur celles du catalogue : celui qui fixe le cap n'a
 * pas forcément le droit de toucher à la carte.
 */
function useTargetProducts(eventId: string | undefined, kind: TargetKind) {
  const { i18n } = useTranslation()
  return useAdminQuery<TargetProduct[]>(
    ['admin-forecast-products', eventId, kind, i18n.language],
    `/admin/events/${eventId}/forecast/products?item_type=${kind}&locale=${i18n.language}`,
    eventId,
  )
}

/** « Toute la famille » ou un produit précis. Chaîne vide = toute la famille. */
function ProductField({
  products,
  value,
  onChange,
}: {
  products: TargetProduct[]
  value: string
  onChange: (value: string) => void
}) {
  const { t } = useTranslation()
  return (
    <SelectField
      label={t('admin.forecast.product')}
      hint={t('admin.forecast.productHint')}
      value={value}
      onChange={onChange}
      options={[
        { value: '', label: t('admin.forecast.productWholeFamily') },
        ...products.map((product) => ({ value: product.id, label: product.label })),
      ]}
    />
  )
}

/** Ajouter un objectif : une famille, un libellé, une quantité, un prix. */
function TargetForm({
  currency,
  eventId,
  onDone,
}: {
  currency: string
  eventId: string | undefined
  onDone: () => void
}) {
  const { t } = useTranslation()
  const [kind, setKind] = useState<TargetKind>('drink_table')
  const [refId, setRefId] = useState('')
  const [label, setLabel] = useState('')
  const [quantity, setQuantity] = useState<number | null>(null)
  const [price, setPrice] = useState<number | null>(null)
  const products = useTargetProducts(eventId, kind).data ?? []

  const create = useAdminMutation<SalesTarget, Record<string, unknown>>(
    'post',
    () => `/admin/events/${eventId}/forecast/lines`,
    eventId,
    [['admin-forecast', eventId ?? '']],
  )

  return (
    <Card className="mt-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          label={t('admin.forecast.kind')}
          hint={t('admin.forecast.kindHint')}
          value={kind}
          onChange={(value) => {
            // Un produit n'appartient qu'à une famille : changer de famille
            // remet le choix à « toute la famille ».
            setKind(value)
            setRefId('')
          }}
          options={KINDS.map((value) => ({
            value,
            label: t(`admin.forecast.kinds.${value}`),
          }))}
        />
        <ProductField
          products={products}
          value={refId}
          onChange={(value) => {
            setRefId(value)
            const product = products.find((row) => row.id === value)
            // Le libellé et le prix du catalogue servent de proposition, à
            // retoucher (prix moyen, remises) — jamais d'écrasement d'une
            // saisie déjà faite.
            if (product) {
              if (!label.trim()) setLabel(product.label)
              if (price === null) setPrice(product.price_minor)
            }
          }}
        />
        <TextField
          label={t('admin.forecast.label')}
          hint={t('admin.forecast.labelHint')}
          value={label}
          onChange={setLabel}
        />
        <NumberField
          label={t('admin.forecast.quantity')}
          hint={t('admin.forecast.quantityHint')}
          value={quantity}
          onChange={setQuantity}
          min={0}
        />
        <MoneyField
          label={t('admin.forecast.unitPrice')}
          hint={t('admin.forecast.unitPriceHint')}
          minor={price}
          currency={currency}
          onChange={setPrice}
        />
      </div>

      {create.error ? (
        <p className="mt-3 text-sm text-state-danger">{create.error.message}</p>
      ) : null}

      <Button
        className="mt-4"
        size="sm"
        disabled={create.isPending || !label.trim()}
        onClick={() =>
          create.mutate(
            {
              item_type: kind,
              ref_id: refId || null,
              label: label.trim(),
              target_quantity: quantity ?? 0,
              unit_price_minor: price ?? 0,
            },
            {
              onSuccess: () => {
                setRefId('')
                setLabel('')
                setQuantity(null)
                setPrice(null)
                onDone()
              },
            },
          )
        }
      >
        {t('admin.forecast.create')}
      </Button>
    </Card>
  )
}
